import i18next from 'i18next';
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import {
  PapiDaSharedOriginalsCreate,
  PapiDaSharedOriginalsUpdate,
  PapiRequestDaSharedOriginalsCreate,
  PapiRequestDaSharedOriginalsUpdate,
  PapiDaSharedOriginalsPublish,
  PapiDaSharedDeviationSubmitInit,
  PapiDaSharedOriginalsDeliver,
  PapiRequestDaSharedOriginalsDeliver,
  PapiRequestDaSharedOriginalsUpdateDelivery,
  PapiDaSharedOriginalsUpdateDelivery,
} from '@wix/da-papi-types';
import { sanitizeUrl } from '@wix/da-url';
import { isApiError, requestNapi, requestPuppy } from '@wix/da-http-client';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { getContentFromStorage } from '@wix/da-editor-tiptap/pkg/components/utils/tiptap';
import { SchemaVersion } from '@wix/da-editor-tiptap/pkg/server/types';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  createOriginalDeviation,
  editOriginalDeviation,
  publishOriginalDeviation,
  submitOriginalDelivery,
  updateOriginalDeviation,
} from '../actions/originals';
import {
  OriginalDeviationFieldName,
  OriginalDeviationFormFields,
} from '../../modals/OriginalDeviationPopup/types';
import { OriginalDeliverySubmitFormData } from '../types';

function* throwError(errorMessage: string) {
  yield putErrorNotification(errorMessage);
}

function formDataToRequestData(
  formData: OriginalDeviationFormFields
): PapiRequestDaSharedOriginalsCreate | PapiRequestDaSharedOriginalsUpdate {
  const formDescription = formData[OriginalDeviationFieldName.DESCRIPTION];
  const formQuestions = formData[OriginalDeviationFieldName.QUESTIONS] ?? [];
  const questions = formQuestions.map(question => ({
    questionid: question.questionId,
    editorRaw: question.questionEditor
      ? question.questionEditor()
      : JSON.stringify({
          version: SchemaVersion.V1,
          document: getContentFromStorage(question.question),
        }),
    type: question.type,
    required: question.isRequired,
    answers:
      question.answers &&
      question.answers.map(answer => ({
        answerid: answer.answerId,
        label: answer.answer,
        price_modifier: answer.priceModifier || undefined,
      })),
  }));
  return {
    title: formData[OriginalDeviationFieldName.TITLE],
    is_mature: formData[OriginalDeviationFieldName.MATURE_CONTENT],
    noai: formData[OriginalDeviationFieldName.NO_AI],
    is_ai_generated: formData[OriginalDeviationFieldName.IS_AI_GENERATED],
    tags: formData[OriginalDeviationFieldName.TAGS],
    add_watermark: formData[OriginalDeviationFieldName.ADD_WATERMARK],
    show_in_progress_count:
      formData[OriginalDeviationFieldName.PROGRESS_IS_VISIBLE],
    display_resolution: Number(
      formData[OriginalDeviationFieldName.DISPLAY_RESOLUTION]
    ) as any,
    base_price: formData[OriginalDeviationFieldName.PRICE],
    delivery_time: formData[OriginalDeviationFieldName.DELIVERY_TIME],
    editorRaw: formDescription && formDescription(),
    questions,
  };
}

function* createOriginalDeviationSaga(
  action: ReturnType<typeof createOriginalDeviation>
): SagaIterator {
  const { formData, onSuccess, onError } = action.payload;
  const response: PapiDaSharedOriginalsCreate = yield call(requestNapi, {
    method: 'post',
    url: `/originals/create`,
    data: formDataToRequestData(formData),
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError')
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess, response.deviation);
  }
}

function* updateOriginalDeviationSaga(
  action: ReturnType<typeof updateOriginalDeviation>
): SagaIterator {
  const { deviationId, formData, onSuccess, onError } = action.payload;
  const response: PapiDaSharedOriginalsUpdate = yield call(requestNapi, {
    method: 'post',
    url: `/originals/update`,
    data: {
      deviationid: deviationId,
      ...formDataToRequestData(formData),
    },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError')
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess, response.deviation);
  }
}

function* publishOriginalDeviationSaga(
  action: ReturnType<typeof publishOriginalDeviation>
): SagaIterator {
  const { deviationId, onSuccess, onError } = action.payload;
  const response: PapiDaSharedOriginalsPublish = yield call(requestNapi, {
    method: 'post',
    url: `/originals/publish`,
    data: {
      deviationid: deviationId,
    },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.publishDeviationServerError')
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess);
  }
}

function* editOriginalDeviationSaga(
  action: ReturnType<typeof editOriginalDeviation>
): SagaIterator {
  const { deviationId } = action.payload;

  // Fetch the full init data for the original deviation
  const response: PapiDaSharedDeviationSubmitInit = yield call(requestPuppy, {
    method: 'get',
    url: '/deviation/submit/init',
    params: { deviationid: deviationId },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.initDeviationServerError')
    );
    return;
  }

  // Open the original deviation popup with the fetched init data
  yield put(
    pushModal(ModalType.ORIGINAL_DEVIATION, {
      wrapInModal: false,
      params: {
        initResponse: response,
      },
    })
  );
}

function* handleSaveOriginalDelivery({
  deviationId,
  descriptionState,
  ...formData
}: OriginalDeliverySubmitFormData) {
  const url = '/originals/update_delivery';
  const method = 'post';

  try {
    const data: PapiRequestDaSharedOriginalsUpdateDelivery = {
      deviationid: `${deviationId}`,
      editorRaw: descriptionState as any,
      title: formData.title,
      is_mature: formData.is_mature,
      is_scrap: formData.is_scrap,
      allow_comments: formData.allow_comments,
      allow_free_download: formData.allow_free_download,
      add_watermark: formData.add_watermark,
      display_resolution: formData.display_resolution,
      tags: formData.tags,
      tierids: formData.tierids,
      galleryids: formData.galleryids,
      groups: formData.groups,
      group_folders: formData.group_folders,
      noai: formData.noai,
      is_ai_generated: formData.is_ai_generated,
      notify_watchers: formData.notify_watchers,
      license_options: formData.license_options,
      location_tag: formData.location_tag,
      subject_tag_types: formData.subject_tag_types,
      subject_tags: formData.subject_tags,
      dreamsofart_prompt_hidden: formData.dreamsofart_prompt_hidden,
      published: formData.published,
      folderid: formData.folderid,
      allow_resale: formData.allow_resale,
    };

    // using request as this uses draftJS
    return yield call(requestNapi, {
      method,
      url,
      data,
    });
  } catch (e) {
    yield putErrorNotification(
      i18next.t('deviationSubmit.error.writeDeviationServerError')
    );
  }
}

function* handleDeliverOriginal(
  originalsDeviationId: number,
  responseId: number
): SagaIterator<boolean | PapiDaSharedOriginalsDeliver> {
  const data: PapiRequestDaSharedOriginalsDeliver = {
    originals_deviationid: originalsDeviationId,
    responseid: responseId,
  };
  const response: PapiDaSharedOriginalsDeliver = yield call(requestPuppy, {
    method: 'post',
    url: '/originals/deliver',
    data,
  });

  if (!response || isApiError(response)) {
    yield putErrorNotification(
      i18next.t('deviationSubmit.error.writeDeviationServerError')
    );
    return false;
  }
  return response;
}

function* submitOriginalDeliverySaga(
  action: ReturnType<typeof submitOriginalDelivery>
): SagaIterator {
  const { formData, doDeliver, onSuccess, onError } = action.payload;
  const { originalsDeviationId, originalsResponseId } = formData;
  if (!originalsDeviationId || !originalsResponseId) {
    return;
  }

  const updateResponse: PapiDaSharedOriginalsUpdateDelivery = yield call(
    handleSaveOriginalDelivery,
    formData
  );

  if (!updateResponse || isApiError(updateResponse)) {
    const errorMessage = updateResponse
      ? updateResponse.errorDescription
      : i18next.t('deviationSubmit.error.writeDeviationServerError');
    yield putErrorNotification(errorMessage);
    if (onError) {
      yield call(onError, formData.deviationId);
    }
    return;
  }

  if (doDeliver) {
    if (originalsDeviationId && originalsResponseId) {
      const response: PapiDaSharedOriginalsDeliver = yield call(
        handleDeliverOriginal,
        originalsDeviationId,
        originalsResponseId
      );
      if (response?.success && response.deviation.url) {
        // redirect to delivered original devpage
        window.location.href = sanitizeUrl(response.deviation.url);
      }
      return;
    }
  }

  if (onSuccess) {
    yield call(onSuccess);
  }
}

export default function* originalsSaga() {
  yield all([
    takeEvery(createOriginalDeviation, createOriginalDeviationSaga),
    takeEvery(updateOriginalDeviation, updateOriginalDeviationSaga),
    takeEvery(publishOriginalDeviation, publishOriginalDeviationSaga),
    takeEvery(editOriginalDeviation, editOriginalDeviationSaga),
    takeEvery(submitOriginalDelivery, submitOriginalDeliverySaga),
  ]);
}
